//path: dashboard/src/pages/OperationsAssociate/MyAssetsPage.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiPackage, FiArrowDownCircle, FiShield, 
  FiClock, FiActivity, FiSearch 
} from "react-icons/fi";

// Components
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import AssetsTabs from "../../components/operationsAssociate/AssetsTabs";
import MyAssetsTable from "../../components/operationsAssociate/MyAssetsTable";
import ReturnAssetModal from "../../components/operationsAssociate/ReturnAssetModal";
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal";

const API_URL = "https://ims.holdthechild.net";

const MyAssetsPage = () => {
    // --- UI State ---
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Data State ---
    const [assets, setAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    const fetchMyAssets = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");
            const response = await axios.get(`${API_URL}/api/assets/my-assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            setError("Custody ledger sync failed.");
            toast.error("Failed to load your assets ❌");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMyAssets(); }, []);

    // --- Modal Handlers ---
    const handleOpenReturnModal = (asset) => {
        setSelectedAsset(asset);
        setIsReturnModalOpen(true);
    };

    const handleOpenViewModal = (asset) => {
        setSelectedAsset(asset);
        setIsViewModalOpen(true);
    };

    // --- UI Components ---
    const CustodyStats = () => (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
                { label: "Active Custody", val: assets.length, icon: FiPackage, color: "text-blue-600", bg: "bg-blue-50" },
                { label: "Pending Returns", val: assets.filter(a => a.status === 'Return Pending').length, icon: FiClock, color: "text-orange-600", bg: "bg-orange-50" },
                { label: "Registry Status", val: "Verified", icon: FiShield, color: "text-emerald-600", bg: "bg-emerald-50" },
            ].map((stat, i) => (
                <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1">{stat.label}</p>
                        <p className={`text-2xl font-black ${stat.color}`}>{stat.val}</p>
                    </div>
                    <div className={`${stat.bg} p-4 rounded-2xl`}>
                        <stat.icon className={stat.color} size={20} />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-6">
                        
                        {/* Tab Hub */}
                        <div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-100 inline-block mb-4">
                            <AssetsTabs />
                        </div>

                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                                    Personal <span className="text-orange-600">Custody</span>
                                </h1>
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1 italic">Assets assigned to your profile</p>
                            </div>
                            
                            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                <FiActivity className="text-emerald-500" /> System Verified
                            </div>
                        </div>

                        <CustodyStats />

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[3rem] border border-gray-100 shadow-sm">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mb-4"></div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Scanning Assignment Ledger...</p>
                            </div>
                        ) : error ? (
                            <div className="p-12 text-center bg-red-50 rounded-[3rem] border border-red-100">
                                <p className="text-red-600 font-black uppercase tracking-widest mb-4">{error}</p>
                                <button onClick={fetchMyAssets} className="bg-red-600 text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">Retry Sync</button>
                            </div>
                        ) : (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-6">
                                <MyAssetsTable
                                    assets={assets}
                                    onReturn={handleOpenReturnModal}
                                    onView={handleOpenViewModal}
                                />
                            </motion.div>
                        )}
                    </div>
                </main>
            </div>

            {/* --- Modals --- */}
            <AnimatePresence>
                {isReturnModalOpen && selectedAsset && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl overflow-hidden"
                        >
                            <ReturnAssetModal
                                asset={selectedAsset}
                                closeModal={() => {
                                    setIsReturnModalOpen(false);
                                    setSelectedAsset(null);
                                    fetchMyAssets();
                                }}
                            />
                        </motion.div>
                    </div>
                )}

                {isViewModalOpen && selectedAsset && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                        >
                            <ViewAssetModal
                                asset={selectedAsset}
                                closeModal={() => {
                                    setIsViewModalOpen(false);
                                    setSelectedAsset(null);
                                }}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MyAssetsPage;